<script setup>
import { ScrollTrigger } from 'gsap/ScrollTrigger';

defineProps({
  table: {
    type: String,
    default: undefined,
  },
  footnote: {
    type: String,
    default: undefined,
  },
  inSidebar: {
    type: Boolean,
    default: false,
  },
});

const globalStore = useGlobalStore();

const wrapperRef = ref(null);
const tableWrapperRef = ref(null);
let tHeadCloneWrapper;

const isSticky = ref(false);

// thead sticky
onMounted(async () => {
  if (!tableWrapperRef.value) return;

  // find thead with "data-sticky" attribute
  const tHead = tableWrapperRef.value.querySelector('thead[data-sticky]');
  if (!tHead) return;

  isSticky.value = true;

  // setup thead clone for animation
  const tHeadClone = tHead.cloneNode(true);
  tHeadCloneWrapper = document.createElement('div');
  tHeadCloneWrapper.classList.add('ctr-table__thead-clone-wrapper');
  tHeadCloneWrapper.appendChild(tHeadClone);
  wrapperRef.value.prepend(tHeadCloneWrapper);

  // setup scroll listeners for scrollLeft sync
  useEventListener(tableWrapperRef, 'scroll', (event) => {
    tHeadCloneWrapper.scrollTo(event.target.scrollLeft, 0);
  });

  let scrollTrigger;

  watch(
    () => globalStore.isSmoothScroll,
    async (isSmoothScroll) => {
      await nextTick();

      if (isSmoothScroll) {
        // calculation functions
        const getTHeadCloneWrapperHeight = () =>
          tHeadCloneWrapper.getBoundingClientRect().height;
        const getTableBodyHeight = () =>
          wrapperRef.value.getBoundingClientRect().height -
          getTHeadCloneWrapperHeight();

        // get layout header for dynamic offset
        const { getLayoutHeaderHeight, getLayoutHeaderBottom } =
          useHeaderRect();

        scrollTrigger = ScrollTrigger.create({
          trigger: wrapperRef.value,
          start: () => `top-=${getLayoutHeaderHeight()} top`,
          end: () => `bottom-=${getTHeadCloneWrapperHeight()} top`,
          onUpdate(self) {
            const offset = getOffsetForPin(
              self.progress,
              getTableBodyHeight(),
              getLayoutHeaderHeight(),
              getLayoutHeaderBottom(),
            );

            tHeadCloneWrapper.style.transform = `translateY(${offset}px)`;
          },
        });
      } else {
        // clear smooth scroll behavior
        scrollTrigger?.kill();
        tHeadCloneWrapper.style.transform = '';
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    scrollTrigger?.kill();
  });
});
</script>

<template>
  <div
    class="ctr-table"
    :class="{
      container: !inSidebar,
      'ctr-table--small': inSidebar,
      'ctr-table--sticky': isSticky,
      'ctr-table--sticky-native': isSticky && !globalStore.isSmoothScroll,
      'ctr-table--sticky-native-header-hide':
        isSticky && !globalStore.isSmoothScroll && !globalStore.headerVisible,
    }"
  >
    <div ref="wrapperRef" class="ctr-table__wrapper">
      <div
        v-if="table"
        ref="tableWrapperRef"
        class="ctr-table__table-wrapper"
        v-html="table"
      />
    </div>

    <div v-if="footnote" class="ctr-table__footnote">
      <UiHtmlContent :content="footnote" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.ctr-table {
  $parent: &;
  $table-width-sm: 1810;
  $table-width-small-sm: 729;
  $border-width: 1px;

  &:deep(#{$parent}__wrapper) {
    text-align: center;
    word-break: break-word;

    @include media-breakpoint-down(sm) {
      margin: 0 em(-$gap-sm);
    }

    table {
      @include media-breakpoint-down(sm) {
        width: em($table-width-sm);
      }
    }

    thead {
      td,
      th {
        font-weight: 400;
        background-color: $color-gray-3;
        position: relative;

        &[data-white] {
          background-color: $color-white;
        }
      }
    }

    td,
    th {
      border: $border-width solid $color-gray-1;
      font-weight: 250;
      font-size: em(18);
      line-height: em(23, 18);
      //padding: em(23, 18) em(24, 18) em(17, 18);
      padding: em(23, 18) em(22, 18) em(17, 18);
      background-color: $color-white;
      position: relative;

      @include media-breakpoint-down(sm) {
        font-size: em(16);
        line-height: em(21, 16);
        padding: em(23, 16) em(24, 16) em(17, 16);
      }

      &[data-border-top] {
        &::before {
          content: '';
          position: absolute;
          top: -$border-width;
          left: -$border-width;
          right: -$border-width;
          height: $border-width;
          background-color: $color-gray-2;
        }
      }

      &[data-spacer] {
        background-color: $color-gray-3;
      }

      &[data-align-center] {
        vertical-align: middle;
      }
    }

    a {
      @include link;
    }

    li {
      list-style-type: none;
      position: relative;

      @include editor-gaps;

      &::before {
        content: '';
        position: absolute;
        top: em(10, 18);
        right: calc(100% + #{em(10, 18)});
        width: 2px;
        height: 2px;
        border-radius: 50%;
        background-color: currentColor;
      }
    }

    img {
      display: inline;
    }

    sup,
    sub {
      font-size: smaller;
    }

    sup {
      vertical-align: super;
    }

    sub {
      vertical-align: sub;
    }
  }

  &__table-wrapper {
    overflow: auto hidden;
    overscroll-behavior: none auto;
    @include hide-scroll;
  }

  &__footnote {
    margin-top: em(43);

    @include media-breakpoint-up(sm) {
      padding: 0;
    }

    @include media-breakpoint-down(sm) {
      margin-top: em(33);
    }
  }

  &--small {
    &:deep(#{$parent}__wrapper) {
      table {
        @include media-breakpoint-down(sm) {
          width: em($table-width-small-sm);
        }
      }
    }

    &#{$parent}--sticky {
      &:deep(#{$parent}__wrapper) {
        #{$parent}__thead-clone-wrapper {
          thead {
            @include media-breakpoint-down(sm) {
              width: em($table-width-small-sm);
            }
          }
        }
      }
    }
  }

  &--sticky {
    &:deep(#{$parent}__wrapper) {
      will-change: transform;

      #{$parent}__thead-clone-wrapper {
        display: flex;
        position: relative;
        z-index: 100;
        overflow: hidden;
        will-change: transform;

        thead {
          border-collapse: collapse;
          width: 100%;
          flex-shrink: 0;

          @include media-breakpoint-down(sm) {
            width: em($table-width-sm);
          }
        }
      }

      table {
        thead {
          td {
            font-size: 0;
            height: 0;
          }
        }
      }
    }

    #{$parent}__table-wrapper {
      margin-top: -$border-width * 2;
    }

    &#{$parent}--sticky-native {
      &:deep(#{$parent}__wrapper) {
        will-change: unset;

        #{$parent}__thead-clone-wrapper {
          position: sticky;
          top: em($header-height);
          transition: top $time-slow;
          will-change: unset;

          @include media-breakpoint-down(sm) {
            top: em($header-height-with-top-gap-sm);
          }
        }
      }

      &#{$parent}--sticky-native-header-hide {
        &:deep(#{$parent}__wrapper) {
          #{$parent}__thead-clone-wrapper {
            @include media-breakpoint-up(sm) {
              top: 0;
            }
          }
        }
      }
    }
  }
}
</style>
